import { Component, inject, OnInit, signal } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/core/services/api.service';
import { ContactInfosService } from 'src/app/core/services/contact-infos.service';
import { Point } from '../../models/point';
import { IContactService } from 'src/app/core/services/i-contact.service';
import { TranslateService } from '@devinforius/super-compos-17';
import { Contact } from '../../models/iContact/contact';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ModelesDocuments } from '../../models/modelesDocuments.model';
import { ParticipantExtraitConforme } from '../../models/participantExtraitConforme.model';
import { IDelibeGlobalService } from 'src/app/core/services/idelibe/global.service';
import { Signatures } from '../../models/signature';
import { DocumentsService } from '@shared/services/documents.service';
import { finalize } from 'rxjs';
import { CompliantExtractGenerationForAPointDto } from '@shared/interfaces/dtos/compliant-extracts';
import { Notifier } from 'src/app/core/services';
import { NotificationError } from 'src/app/core/models';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FormValidators } from '@shared/utils';

@Component({
  selector: 'app-sign-file',
  templateUrl: './sign-file.component.html',
  styleUrls: ['./sign-file.component.scss'],
})
export class SignFileComponent implements OnInit {
  private readonly documentsService = inject(DocumentsService);
  private readonly notifier = inject(Notifier);

  public dateExtrait: Date = new Date();
  public minDate: Date;
  public signatures = [];
  public signatureArray = [];
  public errorIndices: Set<number> = new Set();
  public shouldCompliantExtractPossibilityBeDisabled: boolean = false;
  public errorMessageInsideCard: string = ``;
  public generationErrorMessageTemplate: string = ``;
  public successPointIds: number[] = [];
  public generationSuccessMessageTemplate: string = '';
  public loadingPage: boolean = false;
  public modelesDocPoint: ModelesDocuments[] = [];
  public modeleIdToGenerateInMass: number;
  public numberPointsOfErrors: number = 0;
  public numberPointsOfSuccess: number = 0;
  public openedAccordionError: boolean = false;
  public openedAccordionSuccess: boolean = false;
  public points: Point[];
  private allTypesSeances: ParticipantExtraitConforme[] = [];
  private userList: Contact[] = [];
  private adminList: Contact[] = [];

  public readonly fetchingRightsForCompliantExtracts = signal<boolean>(false);
  public readonly isModelDropdownDisabled = signal<boolean>(false);

  constructor(
    private icontactService: IContactService,
    private translate: TranslateService,
    public api: ApiService,
    public IDelibeGlobalService: IDelibeGlobalService,
    public contactInfosService: ContactInfosService,
    public toastr: ToastrService,
    public spinner: NgxSpinnerService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.minDate = new Date();
  }

  ngOnInit(): void {
    if (this.config.data.massGeneration) {
      if (this.config.data.fromPoint) {
        this.points = this.config.data.points;
      } else {
        this.points = this.config.data.points.filter((point: Point) => point.type === 'point');
      }

      if (this.points.length === 0) {
        this.toastr.error(this.translate.translate('error.noPointSelected'));
        return this.ref.close();
      }

      if (this.config.data?.modeleIdToGenerateInMass) {
        this.modeleIdToGenerateInMass = this.config.data.modelSelected;
      }

      if (this.config.data?.allowModelSelection === false) {
        this.isModelDropdownDisabled.set(true);
      }

      this.getModelDocument();
      this.getRightGeneration();
    }
    this.getInfoParticipant(this.config.data.seanceId);
  }

  getRightGeneration(): void {
    const documentModelId = this.config.data.massGeneration
      ? this.modeleIdToGenerateInMass
      : this.config.data.modelSelected;
    const pointsIds = this.config.data.points.map((point: Point) => point.id);
    const meetingId = this.config.data.seanceId;

    this.generationErrorMessageTemplate = '';
    this.generationSuccessMessageTemplate = '';
    this.numberPointsOfErrors = -1;
    this.numberPointsOfSuccess = -1;
    this.shouldCompliantExtractPossibilityBeDisabled = true;

    if (!documentModelId) return;

    this.fetchingRightsForCompliantExtracts.set(true);

    this.documentsService
      .fetchCompliantExtractsGenerationRights({
        meetingId,
        documentModelId,
        pointsIds,
      })
      .pipe(finalize(() => this.fetchingRightsForCompliantExtracts.set(false)))
      .subscribe({
        next: (rights) => {
          const { points } = rights;
          const isPointInError = (point: CompliantExtractGenerationForAPointDto) =>
            !point.isExtrait ||
            !point.belongToUserService ||
            !point.canGenerateExtraitConf ||
            !point.canUserGenerate ||
            !point.isPointClosed ||
            !point.isExtraitRtfFound;
          const isPointAvailableForGeneration = (point: CompliantExtractGenerationForAPointDto) =>
            point.isExtrait &&
            point.belongToUserService &&
            point.canGenerateExtraitConf &&
            point.canUserGenerate &&
            point.isPointClosed &&
            point.isExtraitRtfFound;
          const pointInErrors = points.filter(isPointInError) ?? [];
          const availablePointsForGeneration = points.filter(isPointAvailableForGeneration) ?? [];
          const areAllPointsInError = points.length === pointInErrors.length;

          this.shouldCompliantExtractPossibilityBeDisabled =
            areAllPointsInError || !availablePointsForGeneration.length;
          this.numberPointsOfErrors = pointInErrors.length;
          this.numberPointsOfSuccess = availablePointsForGeneration.length;
          this.openedAccordionSuccess = !!this.numberPointsOfSuccess;
          this.openedAccordionError = !!this.numberPointsOfErrors;
          this.generateSuccessMessageTemplate(availablePointsForGeneration);
          this.generateErrorMessageTemplate(pointInErrors, { errorCode: rights.errorCode });
        },
        error: () => {
          this.notifier.showError(
            new NotificationError({
              message: 'Erreur lors de la récupération des droits de génération des extraits conformes',
            })
          );
        },
      });
  }

  private getGenerationSuccessMessageTemplate(pointsIds: number[]): string {
    const pointOrderNumbers = (this.config.data.points as Point[])
      .filter((point) => pointsIds.includes(+point.id))
      .map((point) => point.ordreDocs);

    return `<i class="bi bi-check-circle"></i> - ` + pointOrderNumbers.join(', ');
  }

  private getGenerationErrorMessageByErrorCode(errorCode: number): string {
    return `<i class="bi bi-exclamation-diamond mr-1"></i> -  ${this.translate.translate('error._' + (errorCode ?? 14300))}`;
  }

  private generateSuccessMessageTemplate(availablePointsForGeneration: CompliantExtractGenerationForAPointDto[]): void {
    this.generationSuccessMessageTemplate = this.getGenerationSuccessMessageTemplate(
      availablePointsForGeneration.map((point) => point.pointId)
    );
  }

  private generateErrorMessageTemplate(
    pointsInError: CompliantExtractGenerationForAPointDto[],
    errorsDetail: { errorCode: number }
  ): void {
    const pointInErrorsByErrorTypeMap = {
      userCannotGenerate: {
        points: pointsInError.filter((point) => !point.canUserGenerate),
        message: this.translate.translate('error.generateSinglePoint.cantGenerate'),
      },
      doesNotBelongToUserService: {
        points: pointsInError.filter((point) => !point.belongToUserService),
        message: this.translate.translate('error.generateSinglePoint.belongToUserService'),
      },
      isNotCompliantExtract: {
        points: pointsInError.filter((point) => !point.isExtrait),
        message: this.translate.translate('error.generateSinglePoint.noExtrait'),
      },
      notClosedPoint: {
        points: pointsInError.filter((point) => !point.isPointClosed),
        message: this.translate.translate('error.generateSinglePoint.pointNotClosed'),
      },
      isCompliantExtractGenerated: {
        points: pointsInError.filter((point) => !point.isExtraitGenerated),
        message: this.translate.translate('error.generateSinglePoint.extraitNoGenerated'),
      },
      noRtfFoundForCompliantExtract: {
        points: pointsInError.filter((point) => !point.isExtraitRtfFound),
        message: this.translate.translate('error.generateSinglePoint.rtfNotFound'),
      },
    };

    if (pointsInError.length === 1) {
      this.generationErrorMessageTemplate = this.getGenerationErrorMessageByErrorCode(errorsDetail.errorCode);
      return;
    }

    Object.entries(pointInErrorsByErrorTypeMap).forEach(
      (keysvalues: [string, { points: CompliantExtractGenerationForAPointDto[]; message: string }]) => {
        const [errorReason, detail] = keysvalues;

        const getPointsDocumentOrderNumber = (pointsIds: number[]): string[] => {
          return (this.config.data.points as Point[])
            .filter((point) => pointsIds.includes(+point.id))
            .map((point) => point.ordreDocs);
        };

        const pointsDocumentOrderNumbers = getPointsDocumentOrderNumber(detail.points.map((point) => point.pointId));

        if (pointsDocumentOrderNumbers.length) {
          this.generationErrorMessageTemplate += `<i class="bi bi-exclamation-diamond mr-1"></i> - ${detail.message} :  ${pointsDocumentOrderNumbers.join(', ')}</br>`;
        }
      }
    );
  }

  private selectModeleFromConfigIfExists(): void {
    if (this.config.data?.modelSelected) {
      const selectedModele = this.modelesDocPoint.find((modele) => modele.id === this.config.data.modelSelected);

      if (selectedModele) {
        this.modeleIdToGenerateInMass = selectedModele.id;
      }
    }
  }

  getModelDocument(): void {
    const pointIds = this.config.data.points.map((x) => x.id);
    this.IDelibeGlobalService.getGlobalModelesDoc(null, pointIds, true, false, false).subscribe(
      (res: ModelesDocuments[]) => {
        this.modelesDocPoint = res;

        this.selectModeleFromConfigIfExists();
      }
    );
  }

  getInfoParticipant(id: number): void {
    this.loadingPage = true;
    this.api.getParticipantsExtraitConformeByMeetingID(id).subscribe({
      next: (res: ParticipantExtraitConforme[]) => {
        this.getGlobalSignatures();
        this.getGlobalUser();
        this.allTypesSeances = res;
        this.loadingPage = false;
      },
      error: (err: Error) => {
        console.error('Error fetching participant info:', err);
        this.toastr.error(this.translate.translate('error.occurred'));
        this.loadingPage = false;
      },
    });
  }

  // Récupérer toute les signatures
  getGlobalSignatures(): void {
    this.IDelibeGlobalService.getGlobalSignature().subscribe((res: Signatures[]) => {
      this.signatures = res;
      this.setArrayForSignature();
    });
  }

  getGlobalUser(): void {
    const processContacts = (contacts) => {
      return contacts
        .filter((contact) => !contact.nePlusUtiliser)
        .map((contact) => ({
          ...contact,
          fullName: contact.fullName.length > 40 ? contact.fullName.substring(0, 40) + '...' : contact.fullName,
          fullNameTooltip: contact.fullName,
        }));
    };

    this.icontactService.getLowContactsAdmin().subscribe((contactsAdmin) => {
      this.adminList = processContacts(contactsAdmin);
      this.userList = processContacts(this.contactInfosService?.contacts);
      this.contactInfosService.contactsAdmin = contactsAdmin;
      this.setArrayForSignature();
    });
  }

  setArrayForSignature(): void {
    this.signatureArray = this.allTypesSeances
      .filter((participant) => this.signatures.some((signature) => signature.id === participant.signatureId))
      .map((participant) => {
        const signature = this.signatures.find((signature) => signature.id === participant.signatureId);
        return {
          idSignature: participant.signatureId,
          labelSignature: signature?.description ?? null,
          baseIdentifiantDocument: signature?.baseIdentifiantDocument ?? null,
          idAdmin: participant.contactIdiAdmin ?? participant.contactId,
          isContact: !participant.contactIdiAdmin,
          userOptions: participant.contactIdiAdmin ? this.adminList : this.userList,
        };
      });
  }

  editSignatureArray(action: string, index?: number) {
    if (index !== undefined && action === 'remove') {
      this.signatureArray.splice(index, 1);
      this.checkForDuplicates();
      if (this.signatureArray.length === 0) {
        this.shouldCompliantExtractPossibilityBeDisabled = true;
        this.errorMessageInsideCard = this.translate.translate('error.noSignatureEntered');
      }
    } else if (action === 'add') {
      this.signatureArray.push({
        idSignature: null,
        labelSignature: null,
        idAdmin: null,
        isContact: true,
        userOptions: this.userList,
      });
      this.checkForDuplicates();
    } else {
      console.error('Action not found');
    }
  }

  // Méthode pour vérifier les doublons
  checkForDuplicates() {
    this.errorMessageInsideCard = '';
    const baseIds = this.signatureArray.map((item) => item.baseIdentifiantDocument);
    this.errorIndices.clear();
    baseIds.forEach((baseId, index) => {
      if (baseId && baseIds.indexOf(baseId) !== index) {
        this.errorIndices.add(index);
        this.errorIndices.add(baseIds.indexOf(baseId));
      }
    });
    this.shouldCompliantExtractPossibilityBeDisabled = this.errorIndices.size > 0;
    this.shouldCompliantExtractPossibilityBeDisabled
      ? (this.errorMessageInsideCard = this.translate.translate('error.signatureDuplicate'))
      : null;
  }

  updateLabelSignature() {
    this.signatureArray.forEach((item) => {
      const signature = this.signatures.find((signature) => signature.id === item.idSignature);
      item.labelSignature = signature ? signature.description : null;
      item.baseIdentifiantDocument = signature ? signature.baseIdentifiantDocument : null;
    });
    this.checkForDuplicates();
  }

  setUserArray(index: number) {
    const item = this.signatureArray[index];
    if (item.isContact) {
      // si c'est un contact
      item.userOptions = this.userList;
    } else {
      // si c'est un user admin
      item.userOptions = this.adminList;
    }
  }

  confirmSignatures() {
    const isValidDate = (date: Date): boolean => {
      return date instanceof Date && !isNaN(date.getTime());
    };
    if (!isValidDate(this.dateExtrait)) {
      return this.toastr.error(this.translate.translate('error.invalidDate'));
    }
    this.loadingPage = true;
    this.shouldCompliantExtractPossibilityBeDisabled = true;
    this.updateLabelSignature();
    if (!this.config.data.massGeneration) {
      //! si génération unique
      const requestBody = {
        extraitDate: new Date(this.dateExtrait),
        extraitParticipants: this.signatureArray.map((participant) => ({
          signatureId: participant.idSignature,
          contactId: participant.isContact ? participant.idAdmin : 0,
          contactIdiAdmin: participant.isContact ? 0 : participant.idAdmin,
        })),
      };
      const pointId: number = this.config.data.points[0].id;
      this.api.generationExtraitConforme(pointId, this.config.data.modelSelected, requestBody).subscribe(
        (res: any) => {
          this.toastr.success(this.translate.translate('success.requestRecorded'));
          this.loadingPage = false;
          this.shouldCompliantExtractPossibilityBeDisabled = false;
          this.ref.close(true);
        },
        (error) => {
          if (error.status === 403) {
            this.toastr.warning(this.translate.translate('error.noRightAction'));
          } else {
            this.toastr.error(this.translate.translate('error.occurred'));
          }
          this.loadingPage = false;
        }
      );
    } else if (this.config.data.massGeneration) {
      // si en génération en masse
      const requestBody = {
        pointIds: this.config.data.points.map((point: Point) => point.id),
        extraitConformeData: {
          extraitDate: new Date(this.dateExtrait),
          extraitParticipants: this.signatureArray.map((participant) => ({
            signatureId: participant.idSignature,
            contactId: participant.isContact ? participant.idAdmin : 0,
            contactIdiAdmin: participant.isContact ? 0 : participant.idAdmin,
          })),
        },
      };
      this.api
        .massGenerationExtraitConforme(this.config.data.seanceId, this.modeleIdToGenerateInMass, requestBody)
        .subscribe(
          (res: any) => {
            this.toastr.success(this.translate.translate('success.requestRecorded'));
            this.loadingPage = false;
            this.shouldCompliantExtractPossibilityBeDisabled = false;
            this.ref.close(true);
          },
          (error) => {
            if (error.status === 403) {
              this.toastr.warning(this.translate.translate('error.noRightAction'));
            } else {
              this.toastr.error(this.translate.translate('error.occurred'));
            }
            this.loadingPage = false;
          }
        );
    }
  }
}
